import type { Bootcamp, BootcampPricingData } from '../types/bootcamp';

const FUNCTION_BASE_URL = 'https://us-central1-ford-towing-bootcamp.cloudfunctions.net';
const functionPrefix = import.meta.env.VITE_FIREBASE_FUNCTION_PREFIX || '';

/**
 * Get all active bootcamps ordered by event start date
 */
export const getActiveBootcamps = async (): Promise<Bootcamp[]> => {
  const response = await fetch(
    `${FUNCTION_BASE_URL}/${functionPrefix}getActiveBootcamps`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch active bootcamps');
  }

  const data = await response.json();
  return data.bootcamps || [];
};

/**
 * Subscribe to active bootcamps - polls periodically since we're using HTTP functions
 * Returns an unsubscribe function
 */
export const subscribeToActiveBootcamps = (
  callback: (bootcamps: Bootcamp[]) => void,
  pollInterval = 30000 // 30 seconds default
): (() => void) => {
  let isActive = true;

  const fetchBootcamps = async () => {
    if (!isActive) return;

    try {
      const bootcamps = await getActiveBootcamps();
      if (isActive) {
        callback(bootcamps);
      }
    } catch (error) {
      console.error('Error fetching bootcamps:', error);
    }
  };

  // Initial fetch
  fetchBootcamps();

  // Set up polling
  const intervalId = setInterval(fetchBootcamps, pollInterval);

  // Return unsubscribe function
  return () => {
    isActive = false;
    clearInterval(intervalId);
  };
};

/**
 * Get a bootcamp by its URL slug
 */
export const getBootcampBySlug = async (slug: string): Promise<Bootcamp | null> => {
  const response = await fetch(
    `${FUNCTION_BASE_URL}/${functionPrefix}getBootcampBySlug`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug }),
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch bootcamp');
  }

  const data = await response.json();
  return data.bootcamp || null;
};

/**
 * Get a bootcamp by its document ID
 */
export const getBootcampById = async (id: string): Promise<Bootcamp | null> => {
  // For now, fetch all active bootcamps and find by ID
  // Could add a dedicated function if needed
  const bootcamps = await getActiveBootcamps();
  return bootcamps.find(b => b.id === id) || null;
};

/**
 * Fetch pricing for a bootcamp from Stripe via cloud function
 */
export const getBootcampPricing = async (bootcampId: string): Promise<BootcampPricingData> => {
  const response = await fetch(
    `${FUNCTION_BASE_URL}/${functionPrefix}getBootcampPricing`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bootcampId }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch bootcamp pricing');
  }

  return response.json();
};
